import { useSnackbar } from '../contexts/SnackbarProvider';
import { SnackbarType } from '../components/Snackbar';

/**
 * APIエラーの型定義
 */
export interface ApiError {
  response?: {
    status?: number;
    data?: {
      error?: string;
      details?: Array<{
        path: string;
        message: string;
      }>;
    };
    config?: {
      method?: string;
      url?: string;
    };
  };
  message?: string;
}

/**
 * エラーハンドリング用のカスタムフック
 * 
 * TODO: エラーハンドリングの統一化
 * - 他のコンポーネントでも同様のエラーハンドリングパターンを適用
 * - エラーメッセージの国際化対応（i18n）
 * - エラー分類の拡張（ビジネスロジックエラー等）
 */
export const useErrorHandler = () => {
  const showSnackbar = useSnackbar();

  /**
   * エラーの種類とレベルを判定
   * 
   * TODO: パフォーマンス最適化
   * - エラー分類ロジックのメモ化
   * - エラー分類のキャッシュ機能
   */
  const getErrorTypeAndLevel = (error: unknown, context: { component: string; action: string }) => {
    const apiError = error as ApiError;
    
    // 認証関連エラー
    if (context.action === 'login' || context.action === 'signup') {
      if (apiError.response?.status === 401) {
        return {
          type: 'Authentication Failed',
          level: 'warning' as const,
          message: context.action === 'login' 
            ? 'Login failed: Invalid email or password'
            : 'Signup failed: Invalid authentication information'
        };
      }
      if (apiError.response?.status === 400) {
        return {
          type: 'Validation Error',
          level: 'warning' as const,
          message: context.action === 'login'
            ? 'Login failed: Input validation error'
            : 'Signup failed: Input validation error'
        };
      }
      if (apiError.response?.status === 409) {
        return {
          type: 'Duplicate Error',
          level: 'warning' as const,
          message: 'Signup failed: Email already exists'
        };
      }
    }
    
    // 認証関連のコンポーネントでのエラー（actionがなくても判定）
    if (context.component === 'SignInForm' || context.component === 'SignupForm') {
      if (apiError.response?.status === 401) {
        return {
          type: 'Authentication Failed',
          level: 'warning' as const,
          message: 'Authentication failed: Invalid credentials'
        };
      }
      if (apiError.response?.status === 400) {
        return {
          type: 'Validation Error',
          level: 'warning' as const,
          message: 'Input validation error'
        };
      }
    }
    
    // ネットワークエラー
    if (error instanceof Error) {
      if (error.message.includes('network') || error.message.includes('NetworkError')) {
        return {
          type: 'Network Error',
          level: 'error' as const,
          message: 'Network connection error: Unable to connect to server'
        };
      }
      if (error.message.includes('timeout')) {
        return {
          type: 'Timeout Error',
          level: 'error' as const,
          message: 'Timeout error: No response from server'
        };
      }
    }
    
    // その他のAPIエラー
    if (apiError.response?.status) {
      const status = apiError.response.status;
      if (status >= 500) {
        return {
          type: 'Server Error',
          level: 'error' as const,
          message: `Server error (${status}): Internal server error occurred`
        };
      }
      if (status >= 400) {
        return {
          type: 'Client Error',
          level: 'warning' as const,
          message: `Client error (${status}): Request validation failed`
        };
      }
    }
    
    // デフォルト
    return {
      type: 'Unknown Error',
      level: 'error' as const,
      message: 'An unexpected error occurred'
    };
  };

  /**
   * エラーメッセージを取得
   * 
   * TODO: エラーメッセージの改善
   * - エラーメッセージの統一化（定数化）
   * - エラーメッセージの多言語対応
   * - エラーメッセージの動的生成
   */
  const getErrorMessage = (error: unknown, defaultMessage: string): string => {
    const apiError = error as ApiError;
    const backendError = apiError.response?.data?.error;

    if (backendError === 'User already exists') {
      return '既に登録されているメールアドレスです。';
    }
    
    // 認証失敗時の日本語メッセージ
    if (apiError.response?.status === 401) {
      return 'メールアドレスまたはパスワードが正しくありません。';
    }
    
    // バリデーションエラー
    if (apiError.response?.status === 400) {
      return '入力内容を確認してください。';
    }
    
    // 重複エラー
    if (apiError.response?.status === 409) {
      return '既に登録されているメールアドレスです。';
    }
    
    // サーバーエラー
    if (apiError.response?.status && apiError.response.status >= 500) {
      return 'サーバーでエラーが発生しました。しばらく待ってから再度お試しください。';
    }
    
    // バックエンドからのエラーメッセージがある場合
    if (apiError.response?.data?.error) {
      return apiError.response.data.error;
    }
    
    // ネットワークエラー
    if (error instanceof Error) {
      if (error.message.includes('network') || error.message.includes('NetworkError')) {
        return 'ネットワーク接続に問題があります。インターネット接続を確認してください。';
      } else if (error.message.includes('timeout')) {
        return 'サーバーへの接続がタイムアウトしました。しばらく待ってから再度お試しください。';
      }
    }
    
    return defaultMessage;
  };

  /**
   * バリデーションエラーを処理
   */
  const handleValidationErrors = (error: unknown): Record<string, string> => {
    const apiError = error as ApiError;
    const fieldErrors: Record<string, string> = {};
    
    if (apiError.response?.data?.details) {
      apiError.response.data.details.forEach((detail) => {
        fieldErrors[detail.path] = detail.message;
      });
    }
    
    return fieldErrors;
  };

  /**
   * エラーを処理してユーザーに通知
   */
  const handleError = (
    error: unknown,
    context: {
      component: string;
      action: string;
      defaultMessage: string;
      extra?: Record<string, unknown>;
    }
  ) => {
    // エラーメッセージを取得して表示
    const errorMessage = getErrorMessage(error, context.defaultMessage);
    showSnackbar(errorMessage, SnackbarType.ERROR);

    return {
      message: errorMessage,
      fieldErrors: handleValidationErrors(error)
    };
  };

  return {
    getErrorMessage,
    handleValidationErrors,
    handleError
  };
};
